import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Heart, Send, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GiftVoucherPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  
  const [formData, setFormData] = useState({
    serviceId: '',
    senderName: '',
    senderEmail: '',
    recipientName: '',
    recipientEmail: '',
    personalMessage: ''
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('GiftVoucherPage: failed to load services', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.serviceId || !formData.senderName || !formData.senderEmail || 
        !formData.recipientName || !formData.recipientEmail) {
      toast.error('Por favor, completa todos los campos obligatorios');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/gift-vouchers`, {
        service_id: formData.serviceId,
        sender_name: formData.senderName,
        sender_email: formData.senderEmail,
        recipient_name: formData.recipientName,
        recipient_email: formData.recipientEmail,
        personal_message: formData.personalMessage
      });
      
      setVoucherCode(response.data.code);
      setSuccess(true);
      toast.success('¡Bono regalo enviado correctamente!');
    } catch (error) {
      toast.error('Error al crear el bono regalo');
    } finally {
      setSending(false);
    }
  };

  const selectedService = services.find(s => s.id === formData.serviceId);

  if (success) {
    return (
      <div className="min-h-screen bg-halo-cream py-24" data-testid="gift-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-halo-surface rounded-2xl p-12 border border-halo-taupe/10"
          >
            <div className="w-20 h-20 bg-halo-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift size={40} className="text-halo-sage" />
            </div>
            <h1 className="font-heading text-4xl text-halo-charcoal mb-4">
              ¡Bono Regalo Enviado!
            </h1>
            <p className="text-halo-text mb-6">
              Hemos enviado el bono regalo a <strong>{formData.recipientEmail}</strong>
            </p>
            <div className="bg-halo-charcoal rounded-xl p-6 mb-8">
              <p className="text-halo-cream/70 text-sm mb-2">Código del bono</p>
              <p className="font-heading text-3xl text-halo-brown tracking-wider">{voucherCode}</p>
            </div>
            <p className="text-sm text-halo-text mb-8">
              {formData.recipientName} recibirá un email con el código para canjear su {selectedService?.name}. 
              El precio no aparece en el email del destinatario.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => {
                  setSuccess(false);
                  setFormData({
                    serviceId: '',
                    senderName: '',
                    senderEmail: '',
                    recipientName: '',
                    recipientEmail: '',
                    personalMessage: ''
                  });
                }}
                variant="outline"
              >
                Enviar otro regalo
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-halo-brown text-halo-cream hover:bg-halo-charcoal"
              >
                Volver al inicio
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-halo-cream min-h-screen" data-testid="gift-voucher-page">
      {/* Hero */}
      <section className="relative py-24 bg-halo-charcoal overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm flex items-center gap-2">
              <Gift size={16} /> BONOS REGALO
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-halo-cream mt-4 mb-6">
              Regala Bienestar
            </h1>
            <p className="text-halo-cream/80 text-lg leading-relaxed">
              El regalo perfecto para alguien especial. Una experiencia de haloterapia 
              que cuida el cuerpo y la mente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-halo-surface rounded-2xl p-8 border border-halo-taupe/10"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Service Selection */}
              <div>
                <Label className="text-lg font-medium flex items-center gap-2 mb-4">
                  <Heart size={18} className="text-halo-brown" />
                  ¿Qué experiencia quieres regalar?
                </Label>
                <Select
                  value={formData.serviceId}
                  onValueChange={(value) => handleChange('serviceId', value)}
                >
                  <SelectTrigger className="w-full" data-testid="gift-service-select">
                    <SelectValue placeholder="Selecciona una experiencia" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedService && (
                  <p className="text-sm text-halo-text mt-2">
                    {selectedService.description}
                  </p>
                )}
              </div>

              {/* Sender Info */}
              <div className="space-y-4">
                <Label className="text-lg font-medium">Tus datos</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="senderName">Tu nombre *</Label>
                    <Input
                      id="senderName"
                      value={formData.senderName}
                      onChange={(e) => handleChange('senderName', e.target.value)}
                      placeholder="Tu nombre completo"
                      className="mt-2"
                      data-testid="gift-sender-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="senderEmail">Tu email *</Label>
                    <Input
                      id="senderEmail"
                      type="email"
                      value={formData.senderEmail}
                      onChange={(e) => handleChange('senderEmail', e.target.value)}
                      placeholder="tu@email.com"
                      className="mt-2"
                      data-testid="gift-sender-email"
                    />
                  </div>
                </div>
              </div>

              {/* Recipient Info */}
              <div className="space-y-4">
                <Label className="text-lg font-medium">Datos del destinatario</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="recipientName">Nombre del destinatario *</Label>
                    <Input
                      id="recipientName"
                      value={formData.recipientName}
                      onChange={(e) => handleChange('recipientName', e.target.value)}
                      placeholder="Nombre de quien recibe el regalo"
                      className="mt-2"
                      data-testid="gift-recipient-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="recipientEmail">Email del destinatario *</Label>
                    <Input
                      id="recipientEmail"
                      type="email"
                      value={formData.recipientEmail}
                      onChange={(e) => handleChange('recipientEmail', e.target.value)}
                      placeholder="destinatario@email.com"
                      className="mt-2"
                      data-testid="gift-recipient-email"
                    />
                  </div>
                </div>
              </div>

              {/* Personal Message */}
              <div>
                <Label htmlFor="personalMessage">Mensaje personal (opcional)</Label>
                <Textarea
                  id="personalMessage"
                  value={formData.personalMessage}
                  onChange={(e) => handleChange('personalMessage', e.target.value)}
                  placeholder="Escribe un mensaje especial para acompañar tu regalo..."
                  className="mt-2"
                  rows={4}
                  data-testid="gift-message"
                />
              </div>

              {/* Info box */}
              <div className="bg-halo-cream rounded-xl p-4 text-sm text-halo-text">
                <p className="flex items-start gap-2">
                  <Gift size={16} className="text-halo-brown mt-0.5 shrink-0" />
                  <span>
                    El destinatario recibirá un email con el código de su bono regalo. 
                    <strong> El precio no se muestra</strong> en el email del destinatario.
                  </span>
                </p>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={sending || loading}
                className="w-full bg-halo-brown hover:bg-halo-charcoal text-white py-6 text-lg"
                data-testid="gift-submit"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Enviar Bono Regalo
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GiftVoucherPage;
